import { useState } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { KeyRound, Eye, EyeOff, Check, X } from 'lucide-react';

// Superadmin → Ayarlar: öz giriş şifrəsini dəyişmək (hazırkı + yeni x2).
const MIN_LEN = 12;

const PasswordField = ({ label, value, onChange, autoComplete, invalid }) => {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
            <div className="relative">
                <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    spellCheck={false}
                    className={`w-full rounded-xl border bg-white py-2.5 pl-3 pr-11 font-mono text-sm text-slate-800 transition focus:outline-none focus:ring-4 ${
                        invalid ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100' : 'border-slate-200 focus:border-sky-400 focus:ring-sky-100'
                    }`}
                />
                <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? 'Şifrəni gizlət' : 'Şifrəni göstər'}
                    title={show ? 'Gizlət' : 'Göstər'}
                    className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
        </div>
    );
};

const Rule = ({ ok, children }) => (
    <li className={`flex items-center gap-1.5 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
        {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
        {children}
    </li>
);

export default function SuperadminPasswordCard() {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);

    const longEnough = next.length >= MIN_LEN;
    const mixed = /[a-zA-Z]/.test(next) && /\d/.test(next) && /[^a-zA-Z0-9]/.test(next);
    const matches = next.length > 0 && next === confirm;
    const differs = next.length > 0 && next !== current;
    const canSave = current && longEnough && matches && differs && !busy;

    const save = async (e) => {
        e.preventDefault();
        if (!canSave) return;
        setBusy(true);
        try {
            const r = await api.put('/superadmin/password', {
                current_password: current, new_password: next, confirm_password: confirm,
            });
            toast.success(r.data?.msg || 'Şifrə yeniləndi.');
            setCurrent(''); setNext(''); setConfirm('');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Şifrə dəyişdirilmədi.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={save} className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 text-[#0090bb]">
                    <KeyRound className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">Giriş şifrəsi</div>
                    <div className="text-xs text-slate-500">Superadmin panelinə giriş şifrəsini dəyişin</div>
                </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                <PasswordField label="Hazırkı şifrə" value={current} onChange={setCurrent} autoComplete="current-password" />
                <PasswordField label="Yeni şifrə" value={next} onChange={setNext} autoComplete="new-password"
                    invalid={next.length > 0 && !longEnough} />
                <PasswordField label="Yeni şifrə (təkrar)" value={confirm} onChange={setConfirm} autoComplete="new-password"
                    invalid={confirm.length > 0 && !matches} />
            </div>

            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs font-medium">
                <Rule ok={longEnough}>Ən az {MIN_LEN} simvol</Rule>
                <Rule ok={mixed}>Hərf, rəqəm və simvol</Rule>
                <Rule ok={matches}>Təkrar uyğundur</Rule>
                <Rule ok={differs}>Hazırkından fərqlidir</Rule>
            </ul>

            <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-400">5 səhv cəhddən sonra 15 dəqiqə gözləmək lazımdır. WhatsApp OTP əvvəlki kimi qalır.</p>
                <button type="submit" disabled={!canSave}
                    className="rounded-xl bg-[#0090bb] px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4] disabled:opacity-40">
                    {busy ? 'Saxlanılır…' : 'Şifrəni yenilə'}
                </button>
            </div>
        </form>
    );
}
